import React, { useEffect, useRef, useMemo } from 'react';
import * as d3 from 'd3';
import Legend, { GScaleLegend } from '../components/Legend';
import { selectDstGuideLevels } from '../utils/dstGScale';

const Bar = ({
  data = [],
  title = '',
  xAxisLabel = '',
  yAxisLabel = '',
  yDomain,                 // optional fixed [min,max]
  guideLines = [],         // optional [{ level, label, value, color }]
  yValueAccessor = (d) => d.value,
  barColor = 'steelblue',
  startDate,               // kept for compatibility (unused here)
  endDate,                 // kept for compatibility (unused here)
  zoomTransform = d3.zoomIdentity, // shared from parent
  onZoom,                  // (transform) => void
  onShiftTimeline,         // kept for compatibility (unused here)
  dragShift,               // kept for compatibility (unused here)
  onHover,                 // ({date,time,value}) | null
}) => {
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const clipIdRef = useRef(`clip-${Math.random().toString(36).slice(2)}`);
   // Outer size + margins			  // Get container dimensions
  const [dimensions, setDimensions] = React.useState({ width: 1000, height: 120 });
  React.useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setDimensions({
          width: Math.max(1, rect.width - 20),
          height: Math.max(1, rect.height - 20)
        });
      }
    };
     updateDimensions();
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, []);
  const { width, height } = dimensions;
  const margin = { top: 30, right: 40, bottom: 20, left: 50 };

  const innerWidth = width - margin.left - margin.right;
  const innerHeight = height - margin.top - margin.bottom;
  // Base X scale (un-zoomed), in inner coordinates [0..innerWidth]
  const x = useMemo(() => {
    const domain = data.length
      ? d3.extent(data, (d) => new Date(d.date))
      : [new Date(), new Date()];
    return d3.scaleUtc().domain(domain).range([0, innerWidth]);
  }, [data, innerWidth]);

  // Y scale (inner coordinates [innerHeight..0])
  const y = useMemo(() => {
    const dataDomain = data.length ? d3.extent(data, yValueAccessor) : [0, 1];
    const domain = Array.isArray(yDomain) ? yDomain : dataDomain;
    const s = d3.scaleLinear().domain(domain).range([innerHeight, 0]);
    if (!yDomain) s.nice();
    if (yDomain) s.clamp(true);
    return s;
  }, [data, yValueAccessor, yDomain, innerHeight]);

  const activeGuides = useMemo(
    () => (guideLines.length ? selectDstGuideLevels(guideLines, y, innerHeight) : []),
    [guideLines, y, innerHeight]
  );

  useEffect(() => {
    const root = d3.select(svgRef.current);
    root.selectAll('*').remove();

    // --- Root SVG + inner container -------------------------------------
    root.attr('width', width).attr('height', height);

    const container = root
      .append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);

    // --- Titles/labels (draw on root, not inside inner coords) ----------
     // --- Titles/labels (draw on root, not inside inner coords) ----------
    root
     .append('text')
     .attr('x', margin.left / 2) // Position near the left margin
     .attr('y', 10) // Position close to the top of the SVG
     .attr('text-anchor', 'start') // Align text to the start (left)
     .style('font-size', '14px') // Slightly larger font for the title
     .style('font-weight', 'bold')
     .text(title);

   // y Axis label
    root
     .append('text')
     .attr('transform', 'rotate(-90)')
     .attr('x', -height / 2)
     .attr('y', 16)
     .attr('text-anchor', 'middle')
     .style('font-size', '12px')
     .text(yAxisLabel);

    // --- Clip path for inner plotting region ----------------------------
    container
      .append('defs')
      .append('clipPath')
      .attr('id', clipIdRef.current)
      .append('rect')
      .attr('x', 0)
      .attr('y', 0)
      .attr('width', innerWidth)
      .attr('height', innerHeight);

    // --- Axes groups -----------------------------------------------------
    const xAxisG = container
      .append('g')
      .attr('class', 'x-axis')
      .attr('transform', `translate(0,${innerHeight})`);

    const yAxisG = container.append('g').attr('class', 'y-axis');

    // Build X axis factory so tick format follows zoomed domain
    const makeXAxis = (xs) =>
      d3
        .axisBottom(xs)
        .ticks(Math.max(2, Math.floor(innerWidth / 200)))
        .tickFormat((d) => {
          const [s, e] = xs.domain();
          const visibleDays = (e - s) / 86400000; // ms -> days
          if (visibleDays <= 20) return d3.timeFormat('%H:%M')(d);
          const q = Math.floor(d.getMonth() / 3) + 1;
          return `Q${q} ${d.getFullYear()}`;
        });

    // Y axis + grid cloned from ticks
    const drawYAxis = (ys) => {
      yAxisG
        .attr('transform', `translate(${innerWidth},0)`)
        .call(d3.axisRight(ys).ticks(5).tickSizeOuter(0))
        .call((g) => g.select('.domain').remove())
        .call((g) =>
          g
            .selectAll('.tick line')
            .clone()
            .attr('x1', -innerWidth)
            .attr('stroke-opacity', 0.2)
            .attr('stroke-dasharray', '2,2')
        );
    };

    // --- G-scale horizontal guide lines (full width) ---------------------
    const guideG = container
      .append('g')
      .attr('class', 'guide-lines')
      .attr('clip-path', `url(#${clipIdRef.current})`);

    const drawGuides = () => {
      guideG.selectAll('*').remove();
      activeGuides.forEach((guide) => {
        const yPos = y(guide.value);

        guideG
          .append('line')
          .attr('x1', 0)
          .attr('x2', innerWidth)
          .attr('y1', yPos)
          .attr('y2', yPos)
          .attr('stroke', guide.color)
          .attr('stroke-width', 3)
          .attr('stroke-opacity', 0.95);

        guideG
          .append('text')
          .attr('x', innerWidth - 4)
          .attr('y', yPos - 4)
          .attr('text-anchor', 'end')
          .style('font-size', '10px')
          .style('font-weight', 'bold')
          .style('fill', guide.color)
          .text(guide.label);
      });
    };

    drawGuides();

    const barsG = container
      .append('g')
      .attr('clip-path', `url(#${clipIdRef.current})`);

    const barWidth = (zx, i) => {
      const t = new Date(data[i].date).getTime();
      const prev = i > 0 ? new Date(data[i - 1].date).getTime() : null;
      const next = i < data.length - 1 ? new Date(data[i + 1].date).getTime() : null;
      let halfMs;
      if (prev != null && next != null) {
        halfMs = Math.min(t - prev, next - t) / 2;
      } else if (next != null) {
        halfMs = (next - t) / 2;
      } else if (prev != null) {
        halfMs = (t - prev) / 2;
      } else {
        halfMs = 30 * 60 * 1000;
      }
      return Math.max(1, zx(new Date(t + halfMs)) - zx(new Date(t - halfMs)));
    };

    const drawBars = (zx) => {
      barsG
        .selectAll('rect.bar')
        .data(data)
        .join('rect')
        .attr('class', 'bar')
        .attr('x', (d, i) => zx(new Date(d.date)) - barWidth(zx, i) / 2)
        .attr('y', (d) => y(yValueAccessor(d)))
        .attr('width', (d, i) => barWidth(zx, i))
        .attr('height', (d) => Math.max(0, innerHeight - y(yValueAccessor(d))))
        .attr('fill', barColor);
    };
 
    // --- Day/Month separators -------------------------------------------
    const sepG = container.append('g').attr('class', 'day-separators');
    const labelG = container.append('g').attr('class', 'day-labels');

    const drawSeparators = (xs) => {
      sepG.selectAll('*').remove();
      labelG.selectAll('*').remove();

      const [s, e] = xs.domain();
      const visibleDays = (e - s) / 86400000;

      if (visibleDays <= 20) {
        const days = d3.timeDay.range(s, e);
        sepG
          .selectAll('line')
          .data(days)
          .join('line')
          .attr('x1', (d) => xs(d))
          .attr('x2', (d) => xs(d))
          .attr('y1', 0)
          .attr('y2', innerHeight)
          .attr('stroke', '#ccc')
          .attr('stroke-width', 1)
          .attr('stroke-dasharray', '4,4');

        labelG
          .selectAll('text')
          .data(days)
          .join('text')
          .attr('x', (d) => xs(d) + 5)
          .attr('y', -10)
          .attr('text-anchor', 'start')
          .style('font-size', '12px')
          .style('font-family', "'Roboto', sans-serif")
          .style('fill', '#666')
          .text((d) => d3.timeFormat('%b %d')(d));
      } else {
        const months = d3.timeMonth.range(s, e);
        sepG
          .selectAll('line')
          .data(months)
          .join('line')
          .attr('x1', (d) => xs(d))
          .attr('x2', (d) => xs(d))
          .attr('y1', 0)
          .attr('y2', innerHeight)
          .attr('stroke', '#999')
          .attr('stroke-width', 1.5)
          .attr('stroke-dasharray', '4,4');

        labelG
          .selectAll('text')
          .data(months)
          .join('text')
          .attr('x', (d) => xs(d) + 5)
          .attr('y', -10)
          .attr('text-anchor', 'start')
          .style('font-size', '12px')
          .style('font-family', "'Roboto', sans-serif")
          .style('fill', '#333')
          .text((d) => d3.timeFormat('%b %Y')(d));
      }
    };

    // Initial axes + separators
    xAxisG.call(makeXAxis(x));
    drawYAxis(y);
    drawSeparators(x);
    drawBars(x);

    // --- Zoom/Pan (single source of truth for pan) -----------------------
    const updateWithTransform = (t) => {
      const zx = t.rescaleX(x);

      xAxisG.call(makeXAxis(zx));
      drawBars(zx);
      drawSeparators(zx);

      return zx;
    };
    const zoom = d3
      .zoom()
      .scaleExtent([1, 20])
      .translateExtent([
        [0, 0],
        [innerWidth, innerHeight],
      ])
      .on('zoom', (event) => {
        const t = event.transform;
        updateWithTransform(t);
        if (onZoom) onZoom(t);
      });

    // Transparent overlay to capture pointer interactions
    const overlay = container
      .append('rect')
      .attr('class', 'overlay')
      .attr('width', innerWidth)
      .attr('height', innerHeight)
      .attr('fill', 'none')
      .attr('pointer-events', 'all');

    // Attach zoom to the root SVG so wheel/drag works across the plot
    root.call(zoom);

    // Apply initial shared transform from parent
    root.call(zoom.transform, zoomTransform);
    let currentZX = updateWithTransform(zoomTransform);

    // --- Hover that respects zoom ----------------------------------------
    overlay
      .on('mousemove', (event) => {
        const [mx] = d3.pointer(event, overlay.node());
        const t = d3.zoomTransform(root.node());
        currentZX = t.rescaleX(x);

        const hoveredDate = currentZX.invert(mx);
        const bisect = d3.bisector((d) => new Date(d.date)).left;
        const i = bisect(data, hoveredDate);
        const d0 = data[i - 1];
        const d1 = data[i];

        let closest = d0 ?? d1 ?? null;
        if (d0 && d1) {
          closest =
            hoveredDate - new Date(d0.date) >
            new Date(d1.date) - hoveredDate
              ? d1
              : d0;
        }

        if (closest && onHover) {
          const dt = new Date(closest.date);
          if (!isNaN(dt)) {
            onHover({ date: dt, time: dt, value: yValueAccessor(closest) });
          }
        }
      })
      .on('mouseleave', () => {
        if (onHover) onHover(null);
      });

    // Cleanup zoom listeners on unmount
    return () => {
      root.on('.zoom', null);
    };
  }, [
    data,
    x,
    y,
    width,
    height,
    innerWidth,
    innerHeight,
    margin.left,
    margin.top,
    yValueAccessor,
    yDomain,
    guideLines,
    activeGuides,
    barColor,
    zoomTransform,
    onZoom,
    onHover,
    dimensions,
  ]);

  return (
    <div
      ref={containerRef}
      className="chart-wrapper"
      style={{ position: 'relative', width: '100%', height: '100%' }}
    >
      <svg ref={svgRef} />
      <div style={{ position: 'absolute', top: 22, left: 35, zIndex: 10 }}>
        {activeGuides.length > 0 && (
          <div style={{ marginBottom: '2px' }}>
            <GScaleLegend guides={activeGuides} />
          </div>
        )}
      </div>
    </div>
  );
};

// Prevent unnecessary re-renders
export default React.memo(Bar, (prev, next) => {
  return (
    prev.data === next.data &&
    prev.yValueAccessor === next.yValueAccessor &&
    prev.guideLines === next.guideLines &&
    prev.yDomain === next.yDomain &&
    prev.barColor === next.barColor &&
    prev.zoomTransform === next.zoomTransform
  );
});
